<template>
  <content-box class="change-plan--wrap" :width="'530px'">
    <p class="title">
      <template v-if="isNew">
        {{ $t('common.planSubscription') }}
      </template>
      <template v-else>
        {{ $t('userInfo.managePlan.changePlan.title') }}
      </template>
    </p>
    <div class="plan-type--wrap">
      <div class="plan-type" :class="paymentType">
        <button type="button"
                class="card"
                :class="{on: paymentType === 'card'}"
                @click="onSelectPaymentType('card')">
          <i class="icon icon--check-white-size20"></i>{{ $t('userInfo.managePlan.planSubscription.button.card') }}
        </button>
        <button type="button"
                class="enterprise"
                :class="{on: paymentType === 'wireTransfer'}"
                @click="onSelectPaymentType('wireTransfer')">
          <i class="icon icon--check-white-size20"></i>{{ $t('userInfo.managePlan.planSubscription.button.wire') }}
        </button>
      </div>
    </div>
    <div class="header" :class="{ active: showPlanList, double: !isNew || planData.isTrial }">
      <p v-if="isNew && !planData.isTrial" class="current-plan" @click="showPlanList = !showPlanList">
        <span class="label">{{ $t('userInfo.managePlan.planSubscription.button.saas') }}</span>
        <strong class="plan-name">{{ newPlanName }} <i class="icon icon--drop-down"></i></strong>
      </p>
      <template v-else>
        <p class="current-plan">
          <span class="label">{{ $t('userInfo.managePlan.changePlan.label.nowUsing') }}</span>
          <strong class="plan-name">{{ planData.planName }}</strong>
        </p>
        <i class="icon icon--arrow-gray-size40"></i>
        <p class="next-plan" @click="showPlanList = !showPlanList">
          <span class="label">{{ $t('userInfo.managePlan.changePlan.label.changeTo') }}</span>
          <strong class="plan-name">{{ newPlanName }} <i class="icon icon--drop-down"></i></strong>
        </p>
      </template>
      <ul class="plan-list">
        <template v-for="item in normalPlans">
          <li v-if="canChangePlan(item.planNumber) && item.planNumber"
              :key="`plan${item.planNumber}`"
              :class="{ selected: item.name === newPlanName }"
              @click="onChangePlan(item.planNumber)">
            {{ item.name }}
          </li>
        </template>
      </ul>
    </div>
    <div class="plan-info--wrap">
      <div class="next-plan">
        <p class="label">{{ newPlanName }} {{ $t('common.price') }}</p>
        <div class="value">
          <p>₩ {{ newPlanPrice | thousand }}</p>
          <span v-if="!this.isUpgrade || useCoupon">({{ $t('common.vat') }})</span>
        </div>
      </div>
      <div class="plan-pv">
        <p class="label">{{ $t('common.providedPV') }}</p>
        <div class="value">
          <p>{{ newPlanPv | thousand }} PV</p>
        </div>
      </div>
      <template v-if="isUpgrade && !useCoupon">
        <div class="current-plan">
          <p class="label">{{ planData.planName }} {{ $t('userInfo.managePlan.changePlan.label.remainingPvDiscount') }}</p>
          <div class="value">
            <p>( - ) ₩ {{ discountPrice | thousand }}</p>
          </div>
        </div>
        <div class="total-price">
          <p class="label">{{ $t('userInfo.managePlan.changePlan.label.totalAmount') }}</p>
          <div class="value">
            <p>₩ {{ totalPrice | thousand }}</p>
            <span>({{ $t('common.vat') }})</span>
            <span>({{ $t('common.cutOffDecimalPoint') }})</span>
          </div>
        </div>
      </template>
    </div>
    <div class="notice--wrap">
      <ul>
        <template v-if="isNew">
          <li>
            <p>
              {{ $t('userInfo.managePlan.changePlan.notice.firstPlan') }}
            </p>
          </li>
          <li>
            <p>
              {{ $t('userInfo.managePlan.changePlan.notice.isNew') }}
            </p>
          </li>
        </template>
        <template v-else-if="isUpgrade">
          <li>
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isUpgrade.desc0')"></p>
          </li>
          <li>
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isUpgrade.desc1')"></p>
          </li>
          <li v-if="!useCoupon">
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isUpgrade.desc2', {planName: planData.planName, newPlanName: newPlanName})"></p>
          </li>
          <li class="red" v-if="userData.pvPurchaseCount > 0">
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isUpgrade.desc3')"></p>
          </li>
        </template>
        <template v-else-if="isDowngrade">
          <li>
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isDowngrade.desc0', {newPlanName: newPlanName, expireDate: expireDateFormat})"></p>
          </li>
          <li>
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isDowngrade.desc', {newPlanName: newPlanName, expireDate: expireDateFormat})"></p>
          </li>
        </template>
      </ul>
    </div>
    <div class="footer">
      <ui-checkbox v-model="isTermsAgree" v-if="paymentType === 'card'">
        <span class="terms-agree">
          {{ $t('userInfo.managePlan.changePlan.footer.checkbox') }}
        </span>
      </ui-checkbox>
      <span class="terms-agree" v-else>
        {{ $t('userInfo.managePlan.planSubscription.wireDescription') }}
      </span>
      <span v-if="hasTermsError" class="terms-agree-validation-text">{{ $t('common.validationErrorText.requiredAgree') }}</span>
      <div class="button--wrap">
        <ui-button class="btn__cancel"
                   @click="onCloseLayer">{{ $t('common.cancel') }}</ui-button>
        <ui-button class="btn__confirm"
                   :primary="true"
                   @click="onConfirm">
          <template v-if="paymentType === 'card'">{{ $t('common.confirm') }}</template>
          <template v-else>{{ $t('userInfo.managePlan.planSubscription.button.contact') }}</template>
        </ui-button>
      </div>
    </div>
    <span class="button-close" @click="onCloseLayer">
      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'> <g fill='none' fill-rule='evenodd' stroke='#444' stroke-linecap='round' stroke-linejoin='round'> <path d='M3 3l10 10M13 3L3 13'/> </g> </svg>
    </span>
  </content-box>
</template>

<script>
import { addDays } from 'date-fns'
import { openContactUs, getLocaleDateStr, thousand } from '@/libs/utils'
import { normalPlans, PLAN_NUMBER } from '@/libs/plan-helper'
import ContentBox from '@/components/ui/ContentBox'
import UiButton from '@/components/ui/UiButton'
import UiCheckbox from '@/components/ui/UiCheckbox'

export default {
  components: {
    ContentBox,
    UiButton,
    UiCheckbox
  },
  props: [
    'userData',
    'planData',
    'newPlanData',
    'paymentType',
    'selectPlan',
    'applyPlanData'
  ],
  data () {
    return {
      hasTermsError: false,
      isTermsAgree: false,
      showPlanList: false,
      normalPlans: normalPlans
    }
  },
  computed: {
    useCoupon () {
      return this.planData.useCoupon
    },
    isNew () {
      return this.planData.canCreate
    },
    isUpgrade () {
      return !this.isNew && this.planData.pv < this.newPlanData.pv
    },
    isDowngrade () {
      return !this.isNew && !this.isUpgrade
    },
    newPlanName () {
      return this.newPlanData.name
    },
    newPlanNumber () {
      return this.newPlanData.planNumber
    },
    newPlanPrice () {
      return this.newPlanData.price
    },
    newPlanPv () {
      return this.newPlanData.pv
    },
    discountPrice () {
      const discount = this.userData.remainPv - (this.userData.pvPurchaseCount + this.userData.rollingPV)
      if (discount > 0) {
        return discount
      }
      return 0
    },
    totalPrice () {
      return this.newPlanData.price - this.discountPrice
    },
    downgradeLimitations () {
      const items = []
      const hasProFeature = this.planData.servicePlanNumber >= PLAN_NUMBER.pro
      const hasStandardFeature = this.planData.servicePlanNumber >= PLAN_NUMBER.standard

      if (hasProFeature && this.newPlanNumber < PLAN_NUMBER.pro) {
        items.push({
          text: 'funnel',
          more: 'funnel'
        })
      }

      if (hasStandardFeature && this.newPlanNumber < PLAN_NUMBER.standard) {
        items.push({
          text: 'bLogin',
          more: 'bLogin'
        })
        items.push({
          text: 'weeklyReport'
        })
      }
      return items
    },
    expireDateFormat () {
      return getLocaleDateStr(addDays(this.planData.endDate, 1))
    }
  },
  created () {
    this.normalPlans = this.normalPlans.filter(plan => {
      return !plan.name.includes('Cloud')
    })
  },
  methods: {
    onSelectPaymentType (value) {
      this.hasTermsError = false
      this.$emit('onPaymentTypeChange', value)
    },
    canChangePlan (planNumber) {
      if (this.planData.isTrial || this.planData.useCoupon) {
        return true
      } else {
        return planNumber !== this.planData.servicePlanNumber
      }
    },
    onChangePlan (value) {
      this.$emit('onPlanNumberChange',
        this.paymentType,
        value
      )
    },
    showLimitation (more) {
      this.$emit('update:downgradeMore', more)
      // this.onCloseLayer()
    },
    onCloseLayer () {
      if (this.$route.query.planType) {
        this.$router.push({ path: this.$route.path, query: {} })
      }
      this.$emit('closeLayer')
    },
    onConfirm () {
      this.hasTermsError = false
      const isPlusPlan = (this.selectPlan && this.selectPlan[0].type2) || String(this.newPlanData.planNumber).startsWith('4')
      const isYearly = (this.selectPlan && this.selectPlan[1].main) || (this.applyPlanData && this.applyPlanData.isYearly)
                        ? this.$t('userInfo.managePlan.changePlan.option.type2.main')
                        : this.$t('userInfo.managePlan.changePlan.option.type2.main2')
      const paymentType = (this.selectPlan && this.selectPlan[2].main) || this.paymentType === 'card'
                          ? this.$t('userInfo.managePlan.changePlan.option.type3.main')
                          : this.$t('userInfo.managePlan.changePlan.option.type3.main2')
      if (this.paymentType !== 'card' || this.$i18n.locale !== 'ko') {
        if (this.$i18n.locale === 'ko') {
          openContactUs(this.userData.userNum,
            this.isNew && !this.planData.isTrial
              ? this.$t('userInfo.managePlan.default.wireForm', {
                product: this.newPlanData.name,
                payments: paymentType,
                isPlusPlan: isPlusPlan ? this.$t('userInfo.managePlan.default.answer.yes') : this.$t('userInfo.managePlan.default.answer.no'),
                period: isYearly,
                amount: 1,
                pv: thousand(this.newPlanData.pv),
                price: thousand(this.newPlanData.price)
              })
              : this.$t('userInfo.managePlan.default.wireFormWithCategory', {
                category: this.$t('common.changePlan'),
                product: this.newPlanData.name,
                payments: paymentType,
                isPlusPlan: isPlusPlan ? this.$t('userInfo.managePlan.default.answer.yes') : this.$t('userInfo.managePlan.default.answer.no'),
                period: isYearly,
                amount: 1,
                pv: thousand(this.newPlanData.pv),
                price: thousand(this.newPlanData.price)
              })
          )
        } else if (this.$i18n.locale === 'en') {
          openContactUs(this.userData.userNum,
            this.isNew && !this.planData.isTrial
              ? this.$t('userInfo.managePlan.default.wireForm', {
                product: this.newPlanData.name,
                payments: paymentType,
                isPlusPlan: isPlusPlan ? this.$t('userInfo.managePlan.default.answer.yes') : this.$t('userInfo.managePlan.default.answer.no'),
                period: isYearly,
                amount: 1,
                pv: thousand(this.newPlanData.pv),
                price: 90
              })
              : this.$t('userInfo.managePlan.default.wireFormWithCategory', {
                category: this.$t('common.changePlan'),
                product: this.newPlanData.name,
                isPlusPlan: isPlusPlan ? this.$t('userInfo.managePlan.default.answer.yes') : this.$t('userInfo.managePlan.default.answer.no'),
                period: isYearly,
                amount: 1,
                pv: thousand(this.newPlanData.pv),
                price: 90
              })
          )
        } else {
          openContactUs(this.userData.userNum,
            this.isNew && !this.planData.isTrial
              ? this.$t('userInfo.managePlan.default.wireForm', {
                product: this.newPlanData.name,
                payments: paymentType,
                isPlusPlan: isPlusPlan ? this.$t('userInfo.managePlan.default.answer.yes') : this.$t('userInfo.managePlan.default.answer.no'),
                period: isYearly,
                amount: 1,
                pv: thousand(this.newPlanData.pv),
                price: 9000
              })
              : this.$t('userInfo.managePlan.default.wireFormWithCategory', {
                category: this.$t('common.changePlan'),
                product: this.newPlanData.name,
                payments: paymentType,
                isPlusPlan: isPlusPlan ? this.$t('userInfo.managePlan.default.answer.yes') : this.$t('userInfo.managePlan.default.answer.no'),
                period: isYearly,
                amount: 1,
                pv: thousand(this.newPlanData.pv),
                price: 9000
              })
          )
        }
        this.onCloseLayer()
        return
      }

      if (!this.isTermsAgree) {
        this.hasTermsError = true
        return
      }

      this.$emit('onConfirm', {
        newPlanNumber: this.newPlanNumber,
        isUpgrade: this.isUpgrade,
        isDowngrade: this.isDowngrade
      })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">

.change-plan--wrap {
  padding: 30px 30px 25px;
  box-sizing: border-box;

  .plan-type--wrap {
    margin-top: 24px;
    text-align: center;

    .plan-type {
      display: inline-block;
      width: 430px;
      border: 1px solid #e0e0e0;
      background-color: #ffffff;
      transition: background-color 0.3s ease, border-color 0.3s ease;
      border-radius: 20px;
      position: relative;

      button {
        width: 210px;
        text-align: center;
        line-height: 36px;
        height: 36px;
        color: #888888;
        position: relative;
        font-size: 15px;
        z-index: 2;
        transition: color 0.3s ease;

        &:first-child {
          box-sizing: border-box;
          padding-right: 5px;
        }

        &:last-child {
          box-sizing: border-box;
          padding-right: 5px;
        }

        i {
          vertical-align: middle;
          transform: translateY(-1px);
          width: 0;
          transition: width 0.3s ease, margin 0.3s ease;
        }

        &.on {
          color: #ffffff;

          i {
            width: 20px;
            margin: 0 20px 0 0;
          }
        }
      }

      &::before {
        content: "";
        position: absolute;
        z-index: 1;
        top: -1px;
        left: -1px;
        background: #ec0147;
        border-radius: 20px;
        transition: left 0.3s cubic-bezier(0.25, 1, 0.5, 1);
        display: block;
        width: 220px;
        height: 38px;
        box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.25);
      }

      &.wireTransfer::before {
        left: 211px;
      }
    }
  }

  .title {
    font-size: 15px;
    font-weight: bold;
    color: #2f2f2f;
  }

  .header {
    margin-top: 30px;
    text-align: center;
    position: relative;

    .plan-list {
      @include positionXcenter;
      z-index: 2;
      top: 70px;
      width: 182px;
      border-radius: 6px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
      background-color: #ffffff;
      transform: translateX(-50%) scaleY(0);
      opacity: 0;
      transform-origin: top;
      transition: transform .2s ease, opacity .1s ease;

      li {
        color: #444444;
        text-align: left;
        padding: 12px 15px;
        font-size: 14px;
        cursor: pointer;

        &:hover {
          background: #ebebeb;
        }

        &:first-child {
          border-top-left-radius: 7px;
          border-top-right-radius: 7px;
        }

        &:last-child {
          border-bottom-left-radius: 7px;
          border-bottom-right-radius: 7px;
        }

        &.selected {
          background-repeat: no-repeat;
          background-position: calc(100% - 15px) center;
          background-image: url(../../../assets/img/icon_check_green_size16.svg);
        }
      }
    }

    &.double .plan-list {
      left: calc(50% + 120px);
    }

    .label {
      font-size: 13px;
      color: #444;
    }
    strong {
      margin-top: 1px;
      display: block;
      font-size: 26px;
      font-weight: bold;
      cursor: pointer;
      color: $brandColor;
    }
    .icon {
      margin: 16px 77px 0;

      &.icon--drop-down {
        margin-right: 0;
        margin-left: 10px;
        transition: transform .2s ease;
      }
    }
    .current-plan,
    .plan-pv,
    .next-plan {
      display: inline-block;
    }

    &.active {
      .icon {
        &.icon--drop-down {
          transform: scaleY(-1);
        }
      }

      .plan-list {
        opacity: 1;
        transform: translateX(-50%) scaleY(1);
      }
    }
  }

  .plan-info--wrap {
    margin-top: 33px;
    padding: 30px;
    box-sizing: border-box;
    border-radius: 5px;
    border: solid 1px #d7d7d7;
    background-color: #f4f4f4;

    .next-plan,
    .plan-pv,
    .current-plan,
    .total-price {
      @include flexXbetween;
      align-items: center;

      .label {
        font-size: 14px;
        color: #666666;
      }

      .value {
        text-align: right;
        p {
          font-size: 16px;
          font-weight: bold;
          color: #2f2f2f;
        }
        span {
          display: inline-block;
          font-size: 12px;
          color: #444444;
          margin-top: 8px;
        }
      }
    }

    .next-plan {
      .value p {
        font-size: 20px;
      }
    }

    .plan-pv {
      border-top: 1px dashed #c1c1c1;
      margin-top: 30px;
      padding-top: 25px;
      padding-bottom: 5px;
      .value p,
      .value span {
        color: $brandColor;
      }
    }

    .current-plan {
      border-top: 1px dashed #d7d7d7;
      margin-top: 26px;
      padding-top: 26px;
      padding-bottom: 26px;
      .value p,
      .value span {
        color: $brandColor;
      }
    }

    .total-price {
      border-top: 2px solid #c1c1c1;
      padding-top: 15px;
    }
  }

  .notice--wrap {
    margin-top: 25px;

    ul > li {
      position: relative;
      padding-left: 20px;
      font-size: 13px;
      line-height: 1.46;
      color: #666666;

      &.red {
        color: #ec0147;
      }

      &:before {
        content: '';
        display: inline-block;
        width: 3px;
        height: 3px;
        background-color: #888;
        position: absolute;
        top: 7px;
        left: 10px;
      }

      &:not(:first-child) {
        margin-top: 10px;
      }

      ol {
        margin-top: 10px;

        li {
          @include flexXbetween;

          &:not(:first-child) {
            margin-top: 5px;
          }

          button {
            font-size: 12px;
            font-weight: bold;
            text-align: right;
            color: #555555;
          }
        }
      }
    }
  }

  .footer {
    margin-top: 25px;
    padding-top: 25px;
    border-top: 1px solid #d8d8d8;
    text-align: center;
    position: relative;

    .button--wrap {
      margin-top: 40px;

      button {
        width: 86px;
      }

      .btn__confirm {
        margin-left: 5px;
      }
    }

    .ui_checkbox {
      margin-left: 0;
    }
    .terms-agree {
      position: relative;
      text-align: left;
      font-size: 13px;
      color: #2f2f2f;
    }
    .terms-agree-validation-text {
      position: absolute;
      top: 50px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 13px;
      color: $brandColor;
      white-space: nowrap;
      display: inline-block;
    }
  }

  .button-close {
    position: absolute;
    padding: 12px;
    top: 0px;
    right: 0px;
    background-position: center;
    border-radius: 100px;
    background-color: transparent;

    svg {
      g {
        stroke: #444444;
        transition: stroke 0.2s ease;
      }
    }
  }
}
</style>
